import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

// Aligné sur rules-engine (routes/geofence-rules/index.ts) et docs/api/geofence-rules.md
export const RULE_TYPES = ['ENTER', 'LEAVE', 'SPEED_IN_ZONE', 'DWELL'] as const;
export type GeofenceRuleType = (typeof RULE_TYPES)[number];

export const MAX_VEHICLES_PER_RULE = 500;
/** Bornes du seuil des types qui en exigent un (km/h pour SPEED_IN_ZONE, minutes pour DWELL). */
export const THRESHOLD_BOUNDS = {
  SPEED_IN_ZONE: { min: 1, max: 300, unit: 'km/h' },
  DWELL: { min: 1, max: 43_200, unit: 'min' },
} as const;

export const RULE_TYPE_LABELS: Record<GeofenceRuleType, string> = {
  ENTER: 'Entrée',
  LEAVE: 'Sortie',
  SPEED_IN_ZONE: 'Vitesse',
  DWELL: 'Stationnement',
};

export const RULE_TYPE_DESCRIPTIONS: Record<GeofenceRuleType, string> = {
  ENTER: 'Alerte quand un véhicule entre dans la zone',
  LEAVE: 'Alerte quand un véhicule sort de la zone',
  SPEED_IN_ZONE: 'Alerte quand un véhicule dépasse une vitesse dans la zone',
  DWELL: 'Alerte quand un véhicule reste trop longtemps dans la zone',
};

export interface GeofenceRule {
  id: string;
  geofenceId: string;
  ruleType: GeofenceRuleType;
  /** null pour ENTER / LEAVE ; km/h (SPEED_IN_ZONE) ou minutes (DWELL) sinon. */
  threshold: number | null;
  active: boolean;
  /** [] = tous les véhicules du tenant, y compris ceux créés plus tard. */
  vehicleIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateGeofenceRuleBody {
  geofenceId: string;
  ruleType: GeofenceRuleType;
  threshold?: number | null;
  vehicleIds?: string[];
  active?: boolean;
}

/** ruleType et geofenceId sont immuables ; vehicleIds remplace l'assignation complète. */
export type UpdateGeofenceRuleBody = Partial<Pick<GeofenceRule, 'threshold' | 'vehicleIds' | 'active'>>;

export const hasThreshold = (t: GeofenceRuleType): t is keyof typeof THRESHOLD_BOUNDS => t in THRESHOLD_BOUNDS;

/** « 60 km/h », « 2 h 30 min », ou null pour ENTER / LEAVE. */
export const fmtThreshold = (r: Pick<GeofenceRule, 'ruleType' | 'threshold'>): string | null => {
  if (r.threshold == null) return null;
  if (r.ruleType === 'SPEED_IN_ZONE') return `${r.threshold} km/h`;
  return fmtMinutes(r.threshold);
};

export const fmtMinutes = (min: number): string => {
  if (min < 60) return `${min} min`;
  const d = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  const m = min % 60;
  return [d && `${d} j`, h && `${h} h`, m && `${m} min`].filter(Boolean).join(' ');
};

export const GEOFENCE_RULE_ERROR_MESSAGES: Record<string, string> = {
  VEHICLE_NOT_IN_TENANT: "Un véhicule n'appartient pas à votre organisation",
  NOT_FOUND: 'Règle ou zone introuvable',
};

export const listGeofenceRules = async (geofenceId: string) =>
  (await api.get<{ data: GeofenceRule[] }>('/geofence-rules', { params: { geofenceId } })).data.data;

export const createGeofenceRule = async (body: CreateGeofenceRuleBody) =>
  (await api.post<GeofenceRule>('/geofence-rules', body)).data;

export const updateGeofenceRule = async (id: string, body: UpdateGeofenceRuleBody) =>
  (await api.patch<GeofenceRule>(`/geofence-rules/${id}`, body)).data;

export const deleteGeofenceRule = async (id: string) => {
  await api.delete(`/geofence-rules/${id}`);
};

const rulesKey = (geofenceId: string) => ['geofence-rules', geofenceId] as const;

export const useGeofenceRules = (geofenceId: string | null) =>
  useQuery({
    queryKey: rulesKey(geofenceId ?? ''),
    queryFn: () => listGeofenceRules(geofenceId!),
    enabled: geofenceId != null,
  });

// Toute écriture relit la liste de la zone, succès ou échec (règle supprimée ailleurs → 404)
export const useCreateGeofenceRule = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createGeofenceRule,
    onSettled: (_r, _e, body) => qc.invalidateQueries({ queryKey: rulesKey(body.geofenceId) }),
  });
};

export const useUpdateGeofenceRule = (geofenceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateGeofenceRuleBody & { id: string }) => updateGeofenceRule(id, body),
    onSettled: () => qc.invalidateQueries({ queryKey: rulesKey(geofenceId) }),
  });
};

export const useDeleteGeofenceRule = (geofenceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteGeofenceRule,
    onSettled: () => qc.invalidateQueries({ queryKey: rulesKey(geofenceId) }),
  });
};
